<template>
  <div class="debug-options">
    <div class="debug-options__header">
      <h3>🏃 Race Simulator</h3>
      <p class="debug-options__subtitle">Add Runners & Control Race Simulation</p>
    </div>
    
    <div class="debug-options__content">
      <!-- Add Runners Section -->
      <div class="debug-section">
        <h4>👥 Add Runners</h4>
        <div class="debug-form-group">
          <label class="debug-label">Number of Runners:</label>
          <input 
            type="number" 
            v-model="totalRunnersTobeAdded" 
            class="debug-input"
            placeholder="Enter number of runners"
            min="1"
            max="100"
            @input="clearMessages"
          />
        </div>
        <div class="debug-buttons">
          <button 
            class="debug-btn debug-btn--primary" 
            @click="onAddRunners"
            :disabled="loading"
          >
            <span v-if="loading">Adding...</span>
            <span v-else>Add Runners</span>
          </button>
          <button 
            class="debug-btn debug-btn--success" 
            @click="onAddtoFirebase"
            :disabled="!runnerExist || runnerExistinFB"
          >
            Join Race
          </button>
        </div>
        <div v-if="hasAdded" class="debug-alert debug-alert--success">
          {{ hasAdded }}
        </div>
        <div v-if="hasError" class="debug-alert debug-alert--error">
          {{ hasError }}
        </div>
      </div>

      <!-- Speed Control Section -->
      <div class="debug-section">
        <h4>🎛️ Speed Control</h4>
        <div class="debug-form-group">
          <label class="debug-label">Update Speed (ms): {{ duration }}</label>
          <input 
            type="range" 
            v-model="duration" 
            min="500" 
            max="5000" 
            step="100"
            class="debug-slider"
          />
          <div class="debug-speed-labels">
            <span>Fast (500ms)</span>
            <span>Slow (5000ms)</span>
          </div>
        </div>
        <div class="debug-buttons">
          <button 
            class="debug-btn debug-btn--success" 
            @click="startSimulation"
            :disabled="!runnerExistinFB || isRunning"
          >
            ▶️ Start Simulation
          </button>
          <button 
            class="debug-btn debug-btn--danger" 
            @click="stopSimulation"
            :disabled="!isRunning"
          >
            ⏹️ Stop Simulation
          </button>
        </div>
        <div v-if="isRunning" class="debug-alert debug-alert--info">
          Simulation running... Updates every {{ duration }}ms
        </div>
      </div>

      <!-- Race Status Section -->
      <div class="debug-section">
        <h4>📊 Race Status</h4>
        <div class="debug-info">
          <div class="debug-info-item">
            <span class="debug-label">Race ID:</span>
            <span class="debug-value">{{ $route.params.raceId }}</span>
          </div>
          <div class="debug-info-item">
            <span class="debug-label">Race Status:</span>
            <span class="debug-value" :class="getStatusClass()">{{ raceStatus }}</span>
          </div>
          <div class="debug-info-item">
            <span class="debug-label">Subscribed Runners:</span>
            <span class="debug-value">{{ subscribedRunners }}</span>
          </div>
          <div class="debug-info-item">
            <span class="debug-label">Simulation Status:</span>
            <span class="debug-value" :class="isRunning ? 'debug-value--active' : 'debug-value--inactive'">
              {{ isRunning ? 'Running' : 'Stopped' }}
            </span>
          </div>
          <div class="debug-info-item">
            <span class="debug-label">Current Time:</span>
            <span class="debug-value">{{ currentTime }}</span>
          </div>
        </div>
      </div>

      <!-- Firebase Status Section -->
      <div class="debug-section">
        <h4>🔥 Firebase Status</h4>
        <div class="debug-info">
          <div class="debug-info-item">
            <span class="debug-label">Runners in DB:</span>
            <span class="debug-value" :class="runnerExist ? 'debug-value--active' : 'debug-value--inactive'">
              {{ runnerExist ? 'Yes' : 'No' }}
            </span>
          </div>
          <div class="debug-info-item">
            <span class="debug-label">Runners in Firebase:</span>
            <span class="debug-value" :class="runnerExistinFB ? 'debug-value--active' : 'debug-value--inactive'">
              {{ runnerExistinFB ? 'Yes' : 'No' }}
            </span>
          </div>
        </div>
        <div v-if="hasAddedToFB" class="debug-alert debug-alert--success">
          {{ hasAddedToFB }}
        </div>
        <div v-if="hasErrorToFB" class="debug-alert debug-alert--error">
          {{ hasErrorToFB }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { db } from '../../../firebase'

export default {
  name: 'DebugOptions',
  data() {
    return {
      currentTime: new Date().toLocaleTimeString(),
      loading: false,
      totalRunnersTobeAdded: null,
      hasAdded: null,
      hasError: null,
      runnerExist: false,
      runnerExistinFB: false,
      hasAddedToFB: null,
      hasErrorToFB: null,
      subscribedRunners: 0,
      raceStatus: 'Unknown',
      duration: 3000,
      isRunning: false,
      simulationInterval: null,
      count: 0,
      token: "Bearer " + (localStorage.getItem("default_auth_token") || localStorage.getItem("default-auth-token") || ""),
      expressApi: null
    }
  },
  created() {
    // Set the correct API URL based on environment
    this.expressApi = this.$expressApi || "http://localhost:4000/"
    console.log('Debug: Using API URL:', this.expressApi)
    
    // Update current time every second
    setInterval(() => {
      this.currentTime = new Date().toLocaleTimeString()
    }, 1000)

    // Initialize race status and check existing runners
    this.initializeRaceData()
  },
  beforeDestroy() {
    // Clean up simulation interval when component is destroyed
    this.stopSimulation()
  },
  methods: {
    async initializeRaceData() {
      const raceId = this.$route.params.raceId
      
      try {
        // Check if runners exist in database
        const runnersResponse = await axios.get(
          `https://app.liverunapp.com/api/races/${raceId}/runners`,
          { headers: { Authorization: this.token } }
        )
        
        if (runnersResponse.data && runnersResponse.data.length > 0) {
          this.runnerExist = true
          this.subscribedRunners = runnersResponse.data.length
        }

        // Check Firebase status
        try {
          const firebaseRef = db.ref(`/${this.$fbDbEntry}/${raceId}/runners`)
          const snapshot = await firebaseRef.once('value')
          if (snapshot.val() && Object.keys(snapshot.val()).length > 0) {
            this.runnerExistinFB = true
          }

          // Listen to race status changes
          this.listenToRaceStatus(raceId)
        } catch (firebaseError) {
          console.error('Firebase connection error:', firebaseError)
        }
      } catch (error) {
        console.error('Error initializing race data:', error)
      }
    },

    listenToRaceStatus(raceId) {
      try {
        const statusRef = db.ref(`/${this.$fbDbEntry}/${raceId}/status`)
        statusRef.on('value', (snapshot) => {
          const status = snapshot.val()
          this.raceStatus = status || 'Unknown'
          
          if (status === 'running' && this.runnerExistinFB && !this.isRunning) {
            // Auto-start simulation when race starts
            this.startSimulation()
          } else if (status === 'finished') {
            this.stopSimulation()
          }
        })
      } catch (error) {
        console.error('Error listening to race status:', error)
      }
    },

    async onAddRunners() {
      if (!this.totalRunnersTobeAdded) {
        this.hasError = "Please fill the number of runners"
        return
      }

      this.loading = true
      this.hasError = null
      this.hasAdded = null

      try {
        const response = await axios.get(
          `${this.expressApi}addRunnerstoRace/${this.$route.params.raceId}/${this.totalRunnersTobeAdded}/0`,
          { headers: { Authorization: this.token } }
        )
        
        this.loading = false
        this.hasAdded = response.data
        this.runnerExist = true
        this.runnerExistinFB = false
        this.subscribedRunners = parseInt(this.subscribedRunners || 0) + parseInt(this.totalRunnersTobeAdded)
        
        // Show success message
        if (this.$toast) {
          this.$toast.success(`Added ${this.totalRunnersTobeAdded} runners to race!`)
        }
      } catch (error) {
        this.loading = false
        this.hasError = "There is something wrong!"
        console.error('Error adding runners:', error)
        
        if (this.$toast) {
          this.$toast.error('Failed to add runners')
        }
      }
    },

    async onAddtoFirebase() {
      this.hasAddedToFB = null
      this.hasErrorToFB = null

      try {
        const response = await axios.get(
          `${this.expressApi}initRace/${this.$route.params.raceId}/0`,
          { headers: { Authorization: this.token } }
        )
        
        this.runnerExistinFB = true
        this.hasAddedToFB = "Runners added to Firebase successfully!"
        
        if (this.$toast) {
          this.$toast.success('Runners joined the race!')
        }
      } catch (error) {
        this.hasErrorToFB = "Failed to add runners to Firebase!"
        console.error('Error adding runners to Firebase:', error)
        
        if (this.$toast) {
          this.$toast.error('Failed to join race')
        }
      }
    },

    startSimulation() {
      if (this.isRunning) return

      this.isRunning = true
      this.count = 0
      
      this.simulationInterval = setInterval(() => {
        this.count++
        const distance = 0.2106508851524 + (this.count + 1000)
        this.updateRunnerStats(this.$route.params.raceId, distance)
      }, this.duration)

      if (this.$toast) {
        this.$toast.info(`Simulation started! Updates every ${this.duration}ms`)
      }
    },

    stopSimulation() {
      if (!this.isRunning) return

      this.isRunning = false
      if (this.simulationInterval) {
        clearInterval(this.simulationInterval)
        this.simulationInterval = null
      }

      if (this.$toast) {
        this.$toast.info('Simulation stopped')
      }
    },

    async updateRunnerStats(raceId, baseDistance) {
      try {
        const runnersRef = db.ref(`/${this.$fbDbEntry}/${raceId}/runners`)
        const snapshot = await runnersRef.once('value')
        const runners = snapshot.val()

        if (runners) {
          console.log(`🔧 Simulation: Updating ${Object.keys(runners).length} runners with base distance ${baseDistance}`)
          
          for (const runnerId in runners) {
            const runnerRef = runnersRef.child(runnerId)
            const newDistance = baseDistance + this.getRandomInt(1000)
            const newPace = this.getRandomInt(19) + '"' + this.getRandomInt(100) + "'"
            
            await runnerRef.update({
              distance: newDistance,
              pace: newPace,
              questionAnswer: "Simulated runner data"
            })
            
            console.log(`🏃 Updated runner ${runnerId}: distance=${newDistance}, pace=${newPace}`)
          }
        } else {
          console.warn('🔧 Simulation: No runners found in Firebase')
        }
      } catch (error) {
        console.error('Error updating runner stats:', error)
      }
    },

    getRandomInt(max) {
      return Math.floor(Math.random() * Math.floor(max))
    },

    getStatusClass() {
      switch (this.raceStatus.toLowerCase()) {
        case 'running':
          return 'debug-value--active'
        case 'finished':
          return 'debug-value--inactive'
        case 'startrun':
          return 'debug-value--warning'
        default:
          return ''
      }
    },

    clearMessages() {
      // Clear success/error messages when user starts typing
      this.hasAdded = null
      this.hasError = null
    }
  }
}
</script>

<style scoped>
.debug-options {
  margin-top: 2rem;
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border: 2px dashed #6c757d;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.debug-options__header {
  text-align: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #dee2e6;
}

.debug-options__header h3 {
  margin: 0;
  color: #495057;
  font-size: 1.5rem;
  font-weight: 600;
}

.debug-options__subtitle {
  margin: 0.5rem 0 0 0;
  color: #6c757d;
  font-size: 0.9rem;
  font-style: italic;
}

.debug-options__content {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 1.5rem;
}

.debug-section {
  background: white;
  padding: 1rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  border-left: 4px solid #007bff;
}

.debug-section h4 {
  margin: 0 0 1rem 0;
  color: #495057;
  font-size: 1.1rem;
  font-weight: 500;
}

.debug-form-group {
  margin-bottom: 1rem;
}

.debug-form-group .debug-label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #495057;
  font-size: 0.9rem;
}

.debug-input {
  width: 100%;
  padding: 0.5rem;
  border: 2px solid #e9ecef;
  border-radius: 6px;
  font-size: 0.9rem;
  transition: border-color 0.2s ease;
}

.debug-input:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
}

.debug-slider {
  width: 100%;
  margin: 0.5rem 0;
  -webkit-appearance: none;
  appearance: none;
  height: 6px;
  background: #e9ecef;
  border-radius: 3px;
  outline: none;
}

.debug-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 20px;
  height: 20px;
  background: #007bff;
  border-radius: 50%;
  cursor: pointer;
  transition: background 0.2s ease;
}

.debug-slider::-webkit-slider-thumb:hover {
  background: #0056b3;
}

.debug-slider::-moz-range-thumb {
  width: 20px;
  height: 20px;
  background: #007bff;
  border-radius: 50%;
  cursor: pointer;
  border: none;
}

.debug-speed-labels {
  display: flex;
  justify-content: space-between;
  font-size: 0.8rem;
  color: #6c757d;
  margin-top: 0.25rem;
}

.debug-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.debug-btn {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.debug-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none !important;
}

.debug-btn:not(:disabled):hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
}

.debug-btn--primary {
  background: #007bff;
  color: white;
}

.debug-btn--primary:not(:disabled):hover {
  background: #0056b3;
}

.debug-btn--secondary {
  background: #6c757d;
  color: white;
}

.debug-btn--secondary:not(:disabled):hover {
  background: #545b62;
}

.debug-btn--success {
  background: #28a745;
  color: white;
}

.debug-btn--success:not(:disabled):hover {
  background: #1e7e34;
}

.debug-btn--warning {
  background: #ffc107;
  color: #212529;
}

.debug-btn--warning:not(:disabled):hover {
  background: #d39e00;
}

.debug-btn--danger {
  background: #dc3545;
  color: white;
}

.debug-btn--danger:not(:disabled):hover {
  background: #bd2130;
}

.debug-btn--info {
  background: #17a2b8;
  color: white;
}

.debug-btn--info:not(:disabled):hover {
  background: #117a8b;
}

.debug-alert {
  margin-top: 1rem;
  padding: 0.75rem;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 500;
}

.debug-alert--success {
  background: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.debug-alert--error {
  background: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

.debug-alert--info {
  background: #d1ecf1;
  color: #0c5460;
  border: 1px solid #bee5eb;
}

.debug-info {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.debug-info-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem;
  background: #f8f9fa;
  border-radius: 4px;
  border: 1px solid #e9ecef;
}

.debug-info-item .debug-label {
  font-weight: 600;
  color: #495057;
  font-size: 0.9rem;
}

.debug-value {
  font-family: 'Courier New', monospace;
  color: #6c757d;
  font-size: 0.85rem;
}

.debug-value--active {
  color: #28a745;
  font-weight: 600;
}

.debug-value--inactive {
  color: #dc3545;
  font-weight: 600;
}

.debug-value--warning {
  color: #ffc107;
  font-weight: 600;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .debug-options__content {
    grid-template-columns: 1fr;
  }
  
  .debug-buttons {
    flex-direction: column;
  }
  
  .debug-btn {
    width: 100%;
    text-align: center;
  }
}
</style>



// WEBPACK FOOTER //
// DebugOptions.vue?d379a278